import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createSpeechService } from '../utils/speechService';

// 创建设置上下文
const SettingsContext = createContext();

// 默认设置
const defaultSettings = {
  voiceSpeed: 3,
  repeatCount: 3,
  repeatInterval: 3,
  wordInterval: 5,
  fontSize: '中',
  notifications: true,
  // 新增语音相关设置
  voiceType: 'baidu', // 'system', 'youdao', 'baidu'
  voiceId: '', // 系统声音ID或有道/百度发音类型ID
};

// 设置提供者组件
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // 从本地存储加载设置
    const savedSettings = localStorage.getItem('app_settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  // 可用的语音类型
  const [availableVoices, setAvailableVoices] = useState({
    system: [],
    youdao: []
  });

  // 初始化并获取可用的语音
  useEffect(() => {
    // 获取系统语音
    const systemSpeech = createSpeechService('system');
    if (systemSpeech.isSupported()) {
      // 处理语音列表可能异步加载的情况
      const loadVoices = () => {
        const voices = systemSpeech.getVoices();
        if (voices.length > 0) {
          setAvailableVoices(prev => ({
            ...prev,
            system: voices
          }));
        }
      };
      
      loadVoices();
      
      // 某些浏览器需要监听voiceschanged事件
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
    }
    
    // 获取有道词典语音
    const youdaoSpeech = createSpeechService('youdao');
    if (youdaoSpeech.isSupported()) {
      setAvailableVoices(prev => ({
        ...prev,
        youdao: youdaoSpeech.getVoices()
      }));
    }
    
    // 获取百度语音
    const baiduSpeech = createSpeechService('baidu');
    if (baiduSpeech.isSupported()) {
      setAvailableVoices(prev => ({
        ...prev,
        baidu: baiduSpeech.getVoices()
      }));
    }

    // asugar语音
    const asugarSpeech = createSpeechService('asugar');
    if (asugarSpeech.isSupported()) {
      setAvailableVoices(prev => ({
       ...prev,
       asugar: asugarSpeech.getVoices()
      }));
    }
    
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  // 更新设置
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      // 保存到本地存储
      localStorage.setItem('app_settings', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // 重置设置
  const resetSettings = useCallback(() => {
    setSettings(defaultSettings);
    localStorage.setItem('app_settings', JSON.stringify(defaultSettings));
  }, []);

  // 试听功能
  const previewVoice = useCallback((text = 'Hello, this is a test') => {
    const speechService = createSpeechService(settings.voiceType);
    
    if (settings.voiceType === 'system') {
      return speechService.speak(text, {
        lang: 'en-US',
        rate: settings.voiceSpeed / 5, // 将1-5的值转换为0.2-1.0
        pitch: 1,
        volume: 1,
        voice: settings.voiceId
      });
    } else if (settings.voiceType === 'youdao') {
      // 有道不支持句子，只能是单词
      text = "hello";
      return speechService.speak(text, {
        audioType: parseInt(settings.voiceId) || 1
      });
    } else if (settings.voiceType === 'baidu') {
      // 百度也只支持单词
      text = "hello";
      return speechService.speak(text, {
        lan: settings.voiceId || 'uk',
        spd: Math.round(settings.voiceSpeed) || 3
      });
    } else if (settings.voiceType === 'asugar') {
      return speechService.speak(text, {
        lan: settings.voiceId || 'uk',
        spd: Math.round(settings.voiceSpeed) || 3
      });
    }
    
    return { cancel: () => {} };
  }, [settings.voiceType, settings.voiceId, settings.voiceSpeed]);

  // 获取听写设置（合并全局设置和自定义设置）
  const getDictationSettings = useCallback((customSettings = {}) => {
    const baseSettings = {
      repeatCount: settings.repeatCount || 3,
      repeatInterval: settings.repeatInterval || 3,
      wordInterval: settings.wordInterval || 5,
      voiceType: settings.voiceType || 'system',
      voiceOptions: {
        lang: 'en-US',
        rate: settings.voiceSpeed ? settings.voiceSpeed / 5 : 0.8,
        pitch: 1,
        volume: 1
      }
    };
    
    // 根据语音类型添加特定选项
    if (settings.voiceType === 'system' && settings.voiceId) {
      baseSettings.voiceOptions.voice = settings.voiceId;
    } else if (settings.voiceType === 'youdao') {
      baseSettings.voiceOptions.audioType = parseInt(settings.voiceId) || 1;
    } else if (settings.voiceType === 'baidu') {
      baseSettings.voiceOptions.lan = settings.voiceId || 'uk';
      baseSettings.voiceOptions.spd = Math.round(settings.voiceSpeed) || 3;
    } else if (settings.voiceType === 'asugar') {
      baseSettings.voiceOptions.lan = settings.voiceId || 'uk';
      baseSettings.voiceOptions.spd = Math.round(settings.voiceSpeed) || 3;
    }
    
    // 合并自定义设置
    return {
      ...baseSettings,
      ...customSettings,
      voiceOptions: {
        ...baseSettings.voiceOptions,
        ...(customSettings.voiceOptions || {})
      }
    };
  }, [settings]);

  const value = {
    settings,
    updateSettings,
    resetSettings,
    getDictationSettings,
    availableVoices,
    previewVoice
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// 自定义钩子，用于访问设置
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};